import {buildDriverFitOptions} from './driver-fit'
import {equipmentHasScheduleConflict} from './equipment-fit'
import {
    bookingFirstDay,
    equipmentMatchesRequirement,
    equipmentOutOfServiceFor,
} from './equipment-requirement-match'
import {isStockEquipmentRequirement} from './stock-equipment'
import type {DispatchTimelineData, JobRequirement, UnassignedJob} from './types'

export type DispatchPickerEmptyState = {
    /** Compact line for the requirement heading. */
    hint: string
    title: string
    message: string
}

type BuildEmptyStateInput = {
    requirement: JobRequirement
    job: UnassignedJob
    data: DispatchTimelineData
    /** When set for stock requirements, 0 means nothing left to assign freely. */
    stockAssignableQuantity?: number | null
    /** Units of this stock type held on other open jobs (stealable). */
    stockHeldElsewhereQuantity?: number | null
}

function emptyState(hint: string, title: string, message: string): DispatchPickerEmptyState {
    return {hint, title, message}
}

/** Context-aware empty copy when the dispatch picker has no assignable candidates. */
export function buildDispatchPickerEmptyState({
    requirement,
    job,
    data,
    stockAssignableQuantity = null,
    stockHeldElsewhereQuantity = null,
}: BuildEmptyStateInput): DispatchPickerEmptyState {
    if (requirement.kind === 'worker') {
        return workerEmptyState(job, data)
    }

    if (isStockEquipmentRequirement(requirement, data.equipment)) {
        const label = requirementLabel(requirement)
        if (stockAssignableQuantity === 0) {
            if ((stockHeldElsewhereQuantity ?? 0) > 0) {
                return emptyState(
                    'Held elsewhere',
                    `No free ${label}`,
                    'Units are held on other open jobs. Reassign to pull them here.',
                )
            }
            return emptyState(
                'None free',
                `No ${label} available`,
                'None are free to assign; units remain unavailable until physically returned or released.',
            )
        }
        return emptyState(
            'Availability unknown',
            `No ${label} available`,
            'Stock availability could not be confirmed for this job.',
        )
    }

    return uniqueEquipmentEmptyState(requirement, job, data)
}

function requirementLabel(requirement: JobRequirement): string {
    return requirement.label.trim() || (requirement.kind === 'worker' ? 'drivers' : 'equipment')
}

function workerEmptyState(
    job: UnassignedJob,
    data: DispatchTimelineData,
): DispatchPickerEmptyState {
    const allocatedIds = new Set(job.allocations.map((entry) => entry.resourceId))
    const pool = data.resources.filter((resource) => !allocatedIds.has(resource.id))

    if (data.resources.length === 0) {
        return emptyState(
            'None on board',
            'No drivers available',
            'No workers are loaded on this board.',
        )
    }

    if (pool.length === 0) {
        return emptyState(
            'All on this job',
            'No drivers available',
            'Every worker is already assigned to this job.',
        )
    }

    const inService = pool.filter((resource) => !resource.outOfService)
    if (inService.length === 0) {
        return emptyState(
            'All out of service',
            'No drivers available',
            'All remaining workers are out of service.',
        )
    }

    if (job.span != null) {
        const fit = buildDriverFitOptions({
            job,
            resources: data.resources,
            data,
        })
        const selectable = fit.filter((option) => !allocatedIds.has(option.driverId))
        if (selectable.length === 0) {
            return emptyState(
                'All booked elsewhere',
                'No drivers available',
                'All remaining workers are booked on overlapping jobs.',
            )
        }
    }

    return emptyState(
        'None available',
        'No drivers available',
        'No workers can take this job right now.',
    )
}

function uniqueEquipmentEmptyState(
    requirement: JobRequirement,
    job: UnassignedJob,
    data: DispatchTimelineData,
): DispatchPickerEmptyState {
    const label = requirementLabel(requirement)
    const title = `No ${label} available`
    const allocatedIds = new Set(job.allocations.map((entry) => entry.resourceId))
    const matching = data.equipment.filter((asset) =>
        equipmentMatchesRequirement(requirement, asset),
    )

    if (matching.length === 0) {
        return emptyState('None in fleet', title, 'No matching units are in the fleet.')
    }

    const notOnThisJob = matching.filter((asset) => !allocatedIds.has(asset.id))
    if (notOnThisJob.length === 0) {
        return emptyState(
            'All on this job',
            title,
            'Every matching unit is already assigned to this job.',
        )
    }

    const firstDay = bookingFirstDay(job)
    const inService = notOnThisJob.filter((asset) => !equipmentOutOfServiceFor(asset, firstDay))
    if (inService.length === 0) {
        return emptyState('All out of service', title, 'All matching units are out of service.')
    }

    const free = inService.filter((asset) => !equipmentHasScheduleConflict(asset.id, job, data))
    if (free.length === 0) {
        return emptyState(
            'All booked elsewhere',
            title,
            'All matching units are booked on other jobs.',
        )
    }

    return emptyState('None free', title, 'No matching units are free for this job.')
}
